import * as SQLite from 'expo-sqlite';
import { Platform } from 'react-native';
import { CREATE_TABLES_SQL, CREATE_INDEXES_SQL, INITIAL_METADATA, MIGRATIONS } from './DatabaseSchema';

/**
 * SQLiteService - Handles all local database operations
 * Provides a bridge between the app and SQLite for offline functionality
 */
export class SQLiteService {
  private static instance: SQLiteService;
  private db: SQLite.SQLiteDatabase | null = null;
  private isInitialized = false;

  private constructor() {}

  static getInstance(): SQLiteService {
    if (!SQLiteService.instance) {
      SQLiteService.instance = new SQLiteService();
    }
    return SQLiteService.instance;
  }

  /**
   * Initialize the database and create tables
   */
  async initializeDatabase(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      console.log('🗄️ Initializing SQLite database...');
      
      // Check if we're in a web environment
      if (Platform.OS === 'web' || typeof window !== 'undefined') {
        console.log('🌐 Web environment detected - SQLite not supported, skipping initialization');
        this.isInitialized = true;
        return;
      }
      
      // Open database using the new API (React Native only)
      this.db = await SQLite.openDatabaseAsync('stsc_offline.db');
      
      // Create tables
      await this.createTables();
      
      // Create indexes
      await this.createIndexes();
      
      // Insert initial metadata
      await this.insertInitialMetadata();
      
      this.isInitialized = true;
      console.log('✅ SQLite database initialized successfully');
      
    } catch (error) {
      console.error('❌ Error initializing database:', error);
      console.log('🌐 Falling back to web-only mode (localStorage)');
      this.isInitialized = true; // Mark as initialized to prevent further attempts
    }
  }

  /**
   * Create all database tables
   */
  private async createTables(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const tableNames = Object.keys(CREATE_TABLES_SQL);
    
    for (const tableName of tableNames) {
      const sql = CREATE_TABLES_SQL[tableName as keyof typeof CREATE_TABLES_SQL];
      await this.db.execAsync(sql);
      console.log(`✅ Created table: ${tableName}`);
    }
  }

  /**
   * Create database indexes for performance
   */
  private async createIndexes(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    for (const indexSql of CREATE_INDEXES_SQL) {
      await this.db.execAsync(indexSql);
    }
    console.log('✅ Created database indexes');
  }

  /**
   * Insert initial metadata
   */
  private async insertInitialMetadata(): Promise<void> {
    for (const metadata of INITIAL_METADATA) {
      await this.setMetadata(metadata.key, metadata.value);
    }
    console.log('✅ Inserted initial metadata');
  }

  /**
   * Generic CRUD Operations
   */

  // CREATE
  async insert(tableName: string, data: Record<string, any>): Promise<string> {
    if (!this.db) {
      console.warn('🌐 SQLite not available (web environment), skipping insert operation');
      return data.id || 'web-' + Date.now().toString();
    }

    const fields = Object.keys(data);
    const values = Object.values(data);
    const placeholders = fields.map(() => '?').join(', ');

    const sql = `INSERT INTO ${tableName} (${fields.join(', ')}) VALUES (${placeholders})`;
    
    try {
      const result = await this.db.runAsync(sql, values);
      
      // Generate ID if not provided (for auto-increment tables like sync_queue)
      let insertedId = data.id;
      if (!insertedId && result.lastInsertRowId) {
        insertedId = result.lastInsertRowId.toString();
      }

      console.log(`✅ Inserted record into ${tableName}:`, insertedId);
      return insertedId;
    } catch (error) {
      console.error(`❌ Error inserting into ${tableName}:`, error);
      throw error;
    }
  }

  // READ
  async select(
    tableName: string, 
    whereClause = '', 
    params: any[] = [],
    orderBy = '',
    limit?: number
  ): Promise<any[]> {
    if (!this.db) {
      console.warn('🌐 SQLite not available (web environment), returning empty result');
      return [];
    }

    let sql = `SELECT * FROM ${tableName}`;
    
    if (whereClause) {
      sql += ` WHERE ${whereClause}`;
    }
    
    if (orderBy) {
      sql += ` ORDER BY ${orderBy}`;
    }
    
    if (limit) {
      sql += ` LIMIT ${limit}`;
    }

    try {
      const result = await this.db.getAllAsync(sql, params);
      return result;
    } catch (error) {
      console.error(`❌ Error selecting from ${tableName}:`, error);
      throw error;
    }
  }

  // UPDATE
  async update(
    tableName: string, 
    data: Record<string, any>, 
    whereClause: string, 
    params: any[] = []
  ): Promise<number> {
    if (!this.db) {
      console.warn('🌐 SQLite not available (web environment), skipping update operation');
      return 0;
    }

    const fields = Object.keys(data);
    const values = Object.values(data);
    const setClause = fields.map(field => `${field} = ?`).join(', ');

    const sql = `UPDATE ${tableName} SET ${setClause}, updated_at = datetime('now') WHERE ${whereClause}`;
    const allParams = [...values, ...params];

    try {
      const result = await this.db.runAsync(sql, allParams);
      console.log(`✅ Updated ${result.changes} records in ${tableName}`);
      return result.changes;
    } catch (error) {
      console.error(`❌ Error updating ${tableName}:`, error);
      throw error;
    }
  }

  // DELETE
  async delete(tableName: string, whereClause: string, params: any[] = []): Promise<number> {
    if (!this.db) {
      console.warn('🌐 SQLite not available (web environment), skipping delete operation');
      return 0;
    }

    const sql = `DELETE FROM ${tableName} WHERE ${whereClause}`;

    try {
      const result = await this.db.runAsync(sql, params);
      console.log(`✅ Deleted ${result.changes} records from ${tableName}`);
      return result.changes;
    } catch (error) {
      console.error(`❌ Error deleting from ${tableName}:`, error);
      throw error;
    }
  }

  /**
   * Sync-related operations
   */

  // Mark record as needing sync
  async markForSync(tableName: string, recordId: string): Promise<void> {
    await this.update(
      tableName,
      { 
        needs_sync: 1, 
        sync_version: Date.now() // Use timestamp as version
      },
      'id = ?',
      [recordId]
    );
  }

  // Get all records that need syncing
  async getRecordsNeedingSync(tableName: string): Promise<any[]> {
    return await this.select(tableName, 'needs_sync = 1', [], 'created_at ASC');
  }

  // Mark record as synced
  async markAsSynced(tableName: string, recordId: string): Promise<void> {
    await this.update(
      tableName,
      { 
        needs_sync: 0, 
        last_synced_at: new Date().toISOString() 
      },
      'id = ?',
      [recordId]
    );
  }

  /**
   * Sync Queue operations
   */

  async addToSyncQueue(
    operation: 'create' | 'update' | 'delete',
    tableName: string,
    recordId: string,
    data?: any,
    priority = 5
  ): Promise<void> {
    if (!this.db) {
      console.warn('🌐 SQLite not available (web environment), skipping sync queue operation');
      return;
    }
    
    await this.insert('sync_queue', {
      operation_type: operation,
      table_name: tableName,
      record_id: recordId,
      data: data ? JSON.stringify(data) : null,
      priority,
      status: 'pending',
      created_at: new Date().toISOString()
    });
  }

  async getPendingSyncItems(limit = 50): Promise<any[]> {
    return await this.select(
      'sync_queue',
      'status = ?',
      ['pending'],
      'priority ASC, created_at ASC',
      limit
    );
  }

  async updateSyncItemStatus(id: number, status: string, error?: string): Promise<void> {
    const updateData: any = { status };
    if (error) {
      updateData.last_error = error;
      updateData.retry_count = 'retry_count + 1';
    }
    
    await this.update('sync_queue', updateData, 'id = ?', [id]);
  }

  async clearCompletedSyncItems(): Promise<void> {
    await this.delete('sync_queue', 'status = ? AND created_at < datetime("now", "-1 day")', ['completed']);
  }

  /**
   * Metadata operations
   */

  async setMetadata(key: string, value: string | null): Promise<void> {
    if (!this.db) {
      console.warn('🌐 SQLite not available (web environment), skipping metadata operation');
      return;
    }
    const existing = await this.select('offline_metadata', 'key = ?', [key]);
    
    if (existing.length > 0) {
      await this.update(
        'offline_metadata',
        { value, updated_at: new Date().toISOString() },
        'key = ?',
        [key]
      );
    } else {
      await this.insert('offline_metadata', {
        key,
        value,
        updated_at: new Date().toISOString()
      });
    }
  }

  async getMetadata(key: string): Promise<string | null> {
    if (!this.db) {
      console.warn('🌐 SQLite not available (web environment), returning null for metadata');
      return null;
    }
    const result = await this.select('offline_metadata', 'key = ?', [key]);
    return result.length > 0 ? result[0].value : null;
  }

  /**
   * Database maintenance
   */

  async getDatabaseStats(): Promise<any> {
    if (!this.db) {
      console.warn('🌐 SQLite not available (web environment), returning empty stats');
      return {
        vehicles: { total: 0, needsSync: 0 },
        drivers: { total: 0, needsSync: 0 },
        vehicle_assignments: { total: 0, needsSync: 0 },
        vehicle_receiving_forms: { total: 0, needsSync: 0 },
        vehicle_deliveries: { total: 0, needsSync: 0 },
        vehicle_damages: { total: 0, needsSync: 0 },
        accounting_transactions: { total: 0, needsSync: 0 },
        sync_queue: { pending: 0 }
      };
    }
    
    const stats: any = {};
    
    const tables = [
      'vehicles', 'drivers', 'vehicle_assignments', 
      'vehicle_receiving_forms', 'vehicle_deliveries',
      'vehicle_damages', 'accounting_transactions'
    ];
    
    for (const table of tables) {
      const countResult = await this.db.getFirstAsync(`SELECT COUNT(*) as count FROM ${table}`);
      const syncCountResult = await this.db.getFirstAsync(`SELECT COUNT(*) as count FROM ${table} WHERE needs_sync = 1`);
      
      stats[table] = {
        total: (countResult as any)?.count || 0,
        needsSync: (syncCountResult as any)?.count || 0
      };
    }
    
    const queueResult = await this.db.getFirstAsync('SELECT COUNT(*) as count FROM sync_queue WHERE status = "pending"');
    stats.sync_queue = {
      pending: (queueResult as any)?.count || 0
    };
    
    return stats;
  }

  async clearAllData(): Promise<void> {
    const tables = [
      'sync_queue', 'vehicle_damages', 'vehicle_deliveries',
      'vehicle_receiving_forms', 'accounting_transactions',
      'vehicle_assignments', 'drivers', 'vehicles', 'offline_metadata'
    ];
    
    for (const table of tables) {
      await this.delete(table, '1=1');
    }
    
    // Re-insert initial metadata
    await this.insertInitialMetadata();
    
    console.log('✅ Cleared all offline data');
  }

  /**
   * Close database connection
   */
  async closeDatabase(): Promise<void> {
    if (this.db) {
      // Note: expo-sqlite doesn't have explicit close method
      this.db = null;
      this.isInitialized = false;
      console.log('✅ Database connection closed');
    }
  }
}

// Export singleton instance
export const sqliteService = SQLiteService.getInstance();